import { Outlet, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import GlobalHeader from './GlobalHeader';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../store/useAuthStore';
import { useNotificationStore } from '../../store/useNotificationStore';
import echo from '../../lib/echo';
import axios from '../../lib/axios';
import { avisoDeEscritorio, parpadearTitulo, prepararSonidos, sonar } from '../../lib/avisos';

export default function Layout() {
    const { user, isImpersonating, leaveImpersonation } = useAuthStore();
    const navigate = useNavigate();
    const [leaving, setLeaving] = useState(false);
    const fetchNotifications = useNotificationStore(state => state.fetchNotifications);

    // Mismo query que la pantalla de preferencias: si la persona apaga un
    // sonido allí, acá se entera sin recargar.
    const { data: prefs } = useQuery({
        queryKey: ['notification-preferences'],
        queryFn: () => axios.get('/api/notification-preferences'),
        enabled: Boolean(user?.id),
        staleTime: 5 * 60 * 1000,
        select: (r) => r.data,
    });
    const prefsRef = useRef(prefs);
    prefsRef.current = prefs;

    useEffect(() => { prepararSonidos(); }, []);

    // Sonido, aviso de escritorio y título que parpadea. Qué tipo suena con
    // cuál lo dice el catálogo del backend; la persona puede apagarlo.
    const avisar = useCallback((n) => {
        const p = prefsRef.current;
        const meta = p?.catalog?.[n.type];
        if (!meta) return;

        if (meta.sound && p?.sounds?.[n.type] !== false) {
            sonar(meta.sound);
        }

        const titulo = n.subject || meta.label;
        if (p?.desktop !== false) {
            avisoDeEscritorio({
                titulo,
                cuerpo: n.message || n.snippet,
                etiqueta: `${n.type}:${n.contact_id ?? n.id}`,
                alHacerClic: () => n.action_url && navigate(n.action_url),
            });
        }
        if (meta.sound) parpadearTitulo(titulo);
    }, [navigate]);

    useEffect(() => {
        if (!user?.tenant_id || !user?.id) return;

        // Listen for global tenant/user notifications
        const channel = echo.private(`App.Models.User.${user.id}`);
        
        channel.notification((notification) => {
            // Re-fetch or append notification to store to update red dots
            fetchNotifications();
            avisar(notification);
        });

        return () => {
            echo.leave(`App.Models.User.${user.id}`);
        };
    }, [user?.tenant_id, user?.id, fetchNotifications, avisar]);

    const handleLeave = async () => {
        setLeaving(true);
        await leaveImpersonation();
        setLeaving(false);
        navigate('/super-admin/tenants');
    };

    return (
        <div className="flex h-screen w-full bg-slate-50 dark:bg-background-dark overflow-hidden">
            {/* Main Navigation Sidebar */}
            <Sidebar />

            <div className="flex flex-1 flex-col overflow-hidden relative">
                {/* Impersonation Banner */}
                {isImpersonating && (
                    <div className="bg-gradient-to-r from-amber-600 to-red-600 text-white px-6 py-2.5 flex items-center justify-between shadow-md z-30 animate-in slide-in-from-top duration-300 shrink-0">
                        <div className="flex items-center gap-2 text-sm font-semibold">
                            <span className="material-symbols-outlined text-[20px] animate-pulse">security</span>
                            <span>
                                Impersonando a <strong className="text-white underline">{user?.name}</strong> ({user?.role === 'admin' ? 'Admin' : user?.role === 'manager' ? 'Manager' : 'Vendedor'}) del tenant <strong className="text-white underline">{user?.tenant?.name}</strong>
                            </span>
                        </div>
                        <button
                            onClick={handleLeave}
                            disabled={leaving}
                            className="flex items-center gap-1.5 px-3 py-1 bg-white/20 hover:bg-white/30 text-white font-bold rounded-lg text-xs transition-all shadow-sm disabled:opacity-50"
                        >
                            {leaving ? (
                                <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                <span className="material-symbols-outlined text-[16px]">logout</span>
                            )}
                            Volver a Super Admin
                        </button>
                    </div>
                )}

                {/* Top Navigation & Actions */}
                <GlobalHeader />

                {/* Main Content Area */}
                <main className="flex-1 overflow-y-auto no-scrollbar bg-slate-50 dark:bg-background-dark/50 p-0">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
